import * as React from 'react'
import cn from 'classnames'
import { observer } from 'mobx-react-lite'
import { Icon } from '@phosphor-icons/react'
import { fadeOutAndVisit } from 'app/packs/src/utils/fade-out-visit'
import { WithAnalytics } from 'components/with-analytics'
import * as SideMenu from 'components/side-menu'
import { sideNavAnalyticsProps } from '../side-nav-analytics-props'
import { ProductId, currentProductId, productById } from '../nav/products'

const PRODUCT_ORDER: ProductId[] = ['job-architecture', 'wfi']

/**
 * The two products as ordinary sidebar links rather than a drop-down, with a
 * rule under them separating the products from the nav of whichever one you
 * are in. An experiment against ProductSwitcher — both read PRODUCTS from
 * nav/products, so whichever wins, there is still one list of what exists.
 *
 * Trades a click to see the other product for two permanent rows: the switcher
 * hides the fact that a second product exists behind a control that looks like
 * a page title, which is the case for trying this.
 */
const ProductRow = ({
  active,
  className,
  href,
  icon: Icon,
  label,
}: {
  active: boolean
  className: string
  href: string
  icon: Icon
  label: string
}) =>
  active ? (
    <a className={className} href={href}>
      <Icon width={16} height={16} aria-hidden="true" />
      <span>{label}</span>
    </a>
  ) : (
    <button
      type="button"
      className={className}
      onClick={() => fadeOutAndVisit(href)}
    >
      <Icon width={16} height={16} aria-hidden="true" />
      <span>{label}</span>
    </button>
  )

export type ProductLinksProps = {
  /**
   * Row classes for a host whose own link styling this should borrow. The WFI
   * shell opts out of the body-level brand layer (see WfiController), so the
   * `.cm-sidenav__item` rules that style these rows in the main app never reach
   * it — it passes its own `.nav` / `.active` instead.
   */
  itemClassName?: string
  activeClassName?: string
}

export const ProductLinks = observer((props: ProductLinksProps) => {
  const { itemClassName, activeClassName } = props
  // Same guard as the switcher: with WFI out of reach there is only one
  // product, and a list of one is just a heading.
  if (!productById('wfi').available()) return null

  const currentId = currentProductId()

  return (
    <div className="cm-product-links">
      <div className="cm-product-links__label">Products</div>

      <SideMenu.MenuSection
        className="mb-0"
        data-element-id="side-nav-product-links"
      >
        {/* Job Architecture first: as rows rather than a menu, the order is
            read as precedence, and it is the product most people are here for.
            The shared PRODUCTS list keeps its own order for the switcher. */}
        {PRODUCT_ORDER.map((id) => productById(id)).map((product) => {
          const isCurrent = product.id === currentId
          const href = product.href()

          // The two products are separate shells: WFI v2 replaces the app
          // chrome and the main app replaces WFI's, so crossing between them
          // needs a real page load. A Turbo frame swap would leave one shell's
          // sidebar on screen beside the other's. Staying put is an ordinary
          // link.
          return (
            <WithAnalytics
              key={product.id}
              {...sideNavAnalyticsProps({ href, title: product.label })}
            >
              {itemClassName ? (
                <ProductRow
                  active={isCurrent}
                  className={cn(itemClassName, isCurrent && activeClassName)}
                  href={href}
                  icon={product.icon}
                  label={product.label}
                />
              ) : isCurrent ? (
                <SideMenu.LinkItem active href={href} icon={product.icon}>
                  {product.label}
                </SideMenu.LinkItem>
              ) : (
                <SideMenu.ButtonItem
                  icon={product.icon}
                  onClick={() => fadeOutAndVisit(href)}
                >
                  {product.label}
                </SideMenu.ButtonItem>
              )}
            </WithAnalytics>
          )
        })}
      </SideMenu.MenuSection>

      <hr className="cm-product-links__divider" />
    </div>
  )
})
